import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Microscope, RefreshCw, Eye, AlertTriangle,
    ChevronLeft, ChevronRight, User, Hash, X,
    CheckCircle2, Clock3, XCircle, Search,
    CalendarDays, ClipboardList, Activity, FileText, Loader2,
    FlaskConical, Save, MapPin, TestTube2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import PathologyServices from '../../shared/ApiServices/PathologyServices';
import LabServices from '../../shared/ApiServices/LabServices';
import {
    normalizePathologyImportStatus,
    elapsedHoursSince,
} from './pathologyUtils';
import { PathologySlideLibrarySkeleton } from '../../shared/custom-ui/skeleton/Pathology';
import { usePathologyPermissions } from './usePathologyPermissions';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 500;
const IMPORT_DISMISS_DELAY_MS = 4000;
const TERMINAL_IMPORT_STATUSES = ['completed', 'succeeded', 'success', 'complete', 'failed', 'error'];
const SUCCESS_IMPORT_STATUSES = ['completed', 'succeeded', 'success', 'complete'];

interface SlideLibraryTest { testId: string; testName: string; }

interface StudyRecord {
    id: string;
    patientId: string;
    patientName: string;
    mrn: string | null;
    appointmentDateTime: string;
    orderDate: string;
    tests: SlideLibraryTest[];
    orderStatus: string;
    slideLibraryStatus: string;
    studyInstanceUID: string;
    imageSetId: string | null;
    datastoreId: string | null;
    canViewSlide: boolean;
    canCreateReport: boolean;
    canCollectSample: boolean;
    createdAt: string;
}

interface ImportSession { uploadId: string; status: string; startedAt: string }

const cleanPatientName = (raw: string): string => {
    if (!raw || raw === 'Unknown') return 'Unknown Patient';
    let n = raw.replace(/\.[a-z]{2,5}$/i, '');
    n = n.replace(/__+/g, ' ').replace(/_/g, ' ');
    n = n.replace(/\s+\d{1,3}\s*YRS?\s*[MF]?\s*/gi, ' ');
    n = n.replace(/\s+\d+\s*(?:MR|CR|DR|XR)\s*\d+/gi, ' ');
    n = n.replace(/\s{2,}/g, ' ').trim();
    return n.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

const mapEntryToRecord = (entry: any, idx: number): StudyRecord => ({
    id: entry.orderId ?? `entry-${idx}`,
    patientId: entry.patientId ?? '',
    patientName: cleanPatientName(entry.patientName ?? ''),
    mrn: entry.mrn ?? null,
    appointmentDateTime: entry.appointmentDateTime ?? '',
    orderDate: entry.orderDate ?? '',
    tests: Array.isArray(entry.tests) ? entry.tests : [],
    orderStatus: entry.orderStatus ?? '',
    slideLibraryStatus: entry.slideLibraryStatus ?? '',
    studyInstanceUID: entry.studyInstanceUid ?? '',
    imageSetId: entry.imageSetId ?? null,
    datastoreId: entry.datastoreId ?? null,
    canViewSlide: Boolean(entry.canViewSlide),
    canCreateReport: Boolean(entry.canCreateReport),
    canCollectSample: Boolean(entry.canCollectSample),
    createdAt: entry.createdAt ?? '',
});

const PALETTE = ['#1D6FB8', '#6C3AC7', '#0C7F99', '#1A7A4A', '#B45309', '#9B1C4A'];
const pickColor = (id: string) => PALETTE[(id.charCodeAt(0) + id.charCodeAt(1 % id.length)) % PALETTE.length];

const formatMrn = (mrn: string | null, patientId: string): string =>
    mrn ? `MRN: ${mrn}` : `ID: ${patientId.replace(/-/g, '').slice(0, 8).toUpperCase()}`;

interface ImpCfg { color: string; bg: string; border: string; icon: React.ElementType; label: string }
const impCfg = (status?: string): ImpCfg => {
    const s = (status ?? '').toLowerCase();
    if (SUCCESS_IMPORT_STATUSES.includes(s))
        return { color: '#15803D', bg: '#F0FDF4', border: '#86EFAC', icon: CheckCircle2, label: 'Import Complete' };
    if (['failed', 'error'].includes(s))
        return { color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', icon: XCircle, label: 'Import Failed' };
    return { color: '#92400E', bg: '#FFFBEB', border: '#FCD34D', icon: Clock3, label: 'Processing' };
};

export const SlideLibrary: React.FC = () => {
    const navigate = useNavigate();
    const { canUploadStudy } = usePathologyPermissions();

    const [records, setRecords] = useState<StudyRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [mrnInput, setMrnInput] = useState('');
    const [patientNameInput, setPatientNameInput] = useState('');
    const [mrnQuery, setMrnQuery] = useState('');
    const [patientNameQuery, setPatientNameQuery] = useState('');

    const [tokenStack, setTokenStack] = useState<(string | undefined)[]>([undefined]);
    const [currentPageIdx, setCurrentPageIdx] = useState(0);
    const [nextToken, setNextToken] = useState<string | undefined>(undefined);
    const [importSessions, setImportSessions] = useState<ImportSession[]>([]);


    const fetchPage = useCallback(async (
        _token: string | undefined, mrn: string, patientName: string
    ) => {
        setLoading(true); setError('');
        try {
            const res = await PathologyServices.getPathologyStudies({
                limit: PAGE_SIZE,
                mrn: mrn || undefined, patientName: patientName || undefined,
            });
            // new response: { entries: [...], count: number }
            const entries: any[] = Array.isArray(res?.entries) ? res.entries
                : Array.isArray(res?.items) ? res.items   // graceful fallback
                    : Array.isArray(res) ? res
                        : [];
            setRecords(entries.map(mapEntryToRecord));
            setNextToken(res?.nextToken ?? res?.next_token ?? undefined);
        } catch (err: any) {
            setRecords([]); setNextToken(undefined);
            setError(err?.response?.data?.message || err?.message || 'Failed to load slide library');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchPage(undefined, '', ''); }, [fetchPage]);

    useEffect(() => {
        const t = setTimeout(() => setMrnQuery(mrnInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [mrnInput]);

    useEffect(() => {
        const t = setTimeout(() => setPatientNameQuery(patientNameInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [patientNameInput]);

    useEffect(() => {
        setTokenStack([undefined]); setCurrentPageIdx(0);
        fetchPage(undefined, mrnQuery, patientNameQuery);

    }, [mrnQuery, patientNameQuery]);

    const handleNext = useCallback(() => {
        if (!nextToken) return;
        const ns = [...tokenStack];
        if (ns.length <= currentPageIdx + 1) ns.push(nextToken);
        setTokenStack(ns); setCurrentPageIdx(p => p + 1);
        fetchPage(nextToken, mrnQuery, patientNameQuery);
    }, [nextToken, tokenStack, currentPageIdx, fetchPage, mrnQuery, patientNameQuery]);

    const handlePrev = useCallback(() => {
        if (!currentPageIdx) return;
        const ni = currentPageIdx - 1; setCurrentPageIdx(ni);
        fetchPage(tokenStack[ni] as string | undefined, mrnQuery, patientNameQuery);
    }, [currentPageIdx, tokenStack, fetchPage, mrnQuery, patientNameQuery]);

    const handleRefresh = useCallback(() => {
        setTokenStack([undefined]); setCurrentPageIdx(0);
        fetchPage(undefined, mrnQuery, patientNameQuery);
    }, [fetchPage, mrnQuery, patientNameQuery]);



    const clearSearch = useCallback(() => { setMrnInput(''); setPatientNameInput(''); }, []);
    const hasSearch = !!(mrnInput.trim() || patientNameInput.trim());
    const hasNext = !!nextToken;
    const hasPrev = currentPageIdx > 0;

    // ── Import polling ──────────────────────────────────────────────────────

    const pollImport = useCallback(async (uploadId: string) => {
        try {
            const res = await PathologyServices.getPathologyImportStatus(uploadId);
            const status = normalizePathologyImportStatus(res);
            setImportSessions(p => p.map(i => i.uploadId === uploadId ? { ...i, status } : i));
            return status;
        } catch { return null; }
    }, []);

    const sessRef = useRef<ImportSession[]>(importSessions);
    useEffect(() => { sessRef.current = importSessions; }, [importSessions]);

    useEffect(() => {
        const id = window.setInterval(async () => {
            const active = sessRef.current.filter(
                s => !TERMINAL_IMPORT_STATUSES.includes((s.status ?? '').toLowerCase())
            );
            if (!active.length) return;
            for (const s of active) {
                const status = await pollImport(s.uploadId);
                if (SUCCESS_IMPORT_STATUSES.includes(status || '')) {
                    handleRefresh();
                    setTimeout(() => setImportSessions(p => p.filter(x => x.uploadId !== s.uploadId)), IMPORT_DISMISS_DELAY_MS);
                }
            }
        }, 5000);
        return () => window.clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pollImport, handleRefresh]);



    const openViewer = (record: StudyRecord) => {
        const uid = record.studyInstanceUID;
        if (!uid) return;
        const params = new URLSearchParams();
        params.set('StudyInstanceUIDs', uid);
        params.set('orderId', record.id);
        if (record.datastoreId) params.set('datastoreId', record.datastoreId);
        if (record.imageSetId) params.set('imageSetId', record.imageSetId);
        navigate(`/microscopy/pathology?${params.toString()}`);
    };

    return (
        <>
            <div className="v2-animate-fade-in flex flex-col gap-4">

                {/* ══ SEARCH TOOLBAR ══ */}
                <div className="flex items-center flex-wrap gap-2 px-4 py-3 rounded-2xl border border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card)]" style={{ boxShadow: 'var(--v2-shadow-card)' }}>
                    {/* Filter label */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(29,111,184,0.1)' }}>
                            <Search className="w-[15px] h-[15px]" style={{ color: '#1D6FB8' }} />
                        </div>
                        <span className="text-[11px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)]">Filter</span>
                    </div>

                    <div className="w-px h-6 bg-[var(--v2-border-subtle)] flex-shrink-0" />

                    {/* MRN */}
                    <div className="relative flex-1 min-w-40">
                        <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-[14px] h-[14px] text-[var(--v2-text-muted)] pointer-events-none" strokeWidth={2} />
                        <input
                            id="pathology-search-mrn"
                            type="text"
                            placeholder="Search by MRN / Patient ID"
                            value={mrnInput}
                            onChange={e => setMrnInput(e.target.value)}
                            className="w-full pl-8 pr-3 py-2 rounded-xl border border-[var(--v2-border-interactive)] bg-[var(--v2-bg-surface)] text-[var(--v2-text-primary)] text-sm outline-none transition-colors"
                        />
                    </div>

                    {/* Patient name */}
                    <div className="relative flex-1 min-w-48">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-[14px] h-[14px] text-[var(--v2-text-muted)] pointer-events-none" strokeWidth={2} />
                        <input
                            id="pathology-search-patient-name"
                            type="text"
                            placeholder="Search by patient name"
                            value={patientNameInput}
                            onChange={e => setPatientNameInput(e.target.value)}
                            className="w-full pl-8 pr-3 py-2 rounded-xl border border-[var(--v2-border-interactive)] bg-[var(--v2-bg-surface)] text-[var(--v2-text-primary)] text-sm outline-none transition-colors"
                        />
                    </div>

                    {hasSearch && (
                        <button type="button" onClick={clearSearch}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[var(--v2-border-subtle)] bg-[var(--v2-bg-surface)] text-sm font-semibold text-[var(--v2-text-muted)] cursor-pointer hover:text-[var(--v2-text-primary)] transition-colors">
                            <X className="w-[13px] h-[13px]" strokeWidth={2.5} /> Clear
                        </button>
                    )}

                    <div className="flex-1" />

                    <button
                        type="button"
                        id="pathology-refresh-btn"
                        onClick={handleRefresh}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[var(--v2-border-subtle)] bg-[var(--v2-bg-surface)] text-sm font-semibold text-[var(--v2-text-secondary)] cursor-pointer disabled:opacity-50 hover:text-[var(--v2-text-primary)] transition-colors"
                    >
                        <RefreshCw className={`w-[14px] h-[14px] ${loading ? 'animate-spin' : ''}`} strokeWidth={2} />
                        Refresh
                    </button>
                </div>

                {/* Active filter pills */}
                {hasSearch && (
                    <div className="flex flex-wrap gap-2">
                        {mrnQuery && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold text-[var(--v2-primary-600)] bg-[var(--v2-primary-50)] border border-[var(--v2-primary-300)]">
                                <Hash className="w-3 h-3" strokeWidth={2.5} />MRN: {mrnQuery}
                            </span>
                        )}
                        {patientNameQuery && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold text-[var(--v2-primary-600)] bg-[var(--v2-primary-50)] border border-[var(--v2-primary-300)]">
                                <User className="w-3 h-3" strokeWidth={2.5} />Patient: {patientNameQuery}
                            </span>
                        )}
                    </div>
                )}

                {/* Import pipeline status */}
                {importSessions.length > 0 && (
                    <div className="rounded-2xl border border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card)] px-4 py-4" style={{ boxShadow: 'var(--v2-shadow-card)' }}>
                        <p className="text-[11px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)] m-0 mb-3">
                            Import Pipeline
                        </p>
                        <div className="flex flex-col gap-2.5">
                            {importSessions.slice(0, 5).map(s => {
                                const cfg = impCfg(s.status);
                                const StatusIcon = cfg.icon;
                                const hrs = elapsedHoursSince(s.startedAt);
                                return (
                                    <div key={s.uploadId} className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5"
                                        style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}>
                                        <div className="flex items-center gap-2.5 min-w-0">
                                            <StatusIcon className="w-4 h-4 flex-shrink-0" strokeWidth={2} style={{ color: cfg.color }} />
                                            <code className="text-sm text-[var(--v2-text-secondary)] overflow-hidden text-ellipsis whitespace-nowrap">{s.uploadId}</code>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {hrs !== null && (
                                                <span className="text-xs font-semibold" style={{ color: cfg.color, opacity: 0.75 }}>
                                                    {hrs < 1 ? `${Math.round(hrs * 60)}m` : `${hrs.toFixed(1)}h`}
                                                </span>
                                            )}
                                            <span className="text-sm font-bold" style={{ color: cfg.color }}>{cfg.label}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ══ SKELETON ══ */}
                {loading && <PathologySlideLibrarySkeleton />}

                {/* ══ PATIENT STUDY LIST ══ */}
                {!loading && (
                    <div className="rounded-[var(--v2-radius-lg)] overflow-hidden border border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card)]" style={{ boxShadow: 'var(--v2-shadow-card)' }}>

                        {/* Column header — 5 data cols + Actions */}
                        <div className="flex items-center px-6 py-3 bg-[var(--v2-bg-surface)] border-b-2 border-[var(--v2-border-subtle)]">
                            <div className="flex-1 grid" style={{ gridTemplateColumns: '1fr 1fr 1.2fr 1.1fr 1.1fr', columnGap: 20 }}>
                                {([
                                    { label: 'Patient', Icon: User },
                                    { label: 'Tests', Icon: ClipboardList },
                                    { label: 'Appointment', Icon: CalendarDays },
                                    { label: 'Order Status', Icon: Activity },
                                    { label: 'Lab Status', Icon: Microscope },
                                ] as const).map(({ label, Icon }) => (
                                    <div key={label} className="flex items-center gap-1.5">
                                        <Icon className="w-[12px] h-[12px] text-[var(--v2-text-muted)] flex-shrink-0" strokeWidth={2} />
                                        <span className="text-[11px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)] whitespace-nowrap">
                                            {label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="w-[160px] flex-shrink-0 flex items-center justify-center">
                                <span className="text-[11px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)]">
                                    Actions
                                </span>
                            </div>
                        </div>

                        {records.length === 0 ? (
                            <EmptyState error={error} hasSearch={hasSearch} canUploadStudy={canUploadStudy} />
                        ) : (
                            records.map((r, i) => (
                                <PatientStudyRow
                                    key={r.id}
                                    record={r}
                                    color={pickColor(r.id)}
                                    isEven={i % 2 === 0}
                                    onOpen={() => openViewer(r)}
                                     onCollectSample={() => {}}
                                     collectingSample={false}
                                />
                            ))
                        )}
                        <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--v2-border-subtle)] bg-[var(--v2-bg-surface)]">
                            <span className="text-sm text-[var(--v2-text-muted)] font-medium">
                                {records.length} record{records.length !== 1 ? 's' : ''} — Page {currentPageIdx + 1}
                            </span>
                            <div className="flex items-center gap-2">
                                <PagBtn id="pathology-prev-page" onClick={handlePrev} disabled={!hasPrev} aria-label="Previous page">
                                    <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
                                    Previous
                                </PagBtn>
                                <span className="px-4 py-1.5 rounded-xl text-sm font-extrabold text-[var(--v2-primary-600)] bg-[var(--v2-primary-50)] border border-[var(--v2-primary-300)]">
                                    {currentPageIdx + 1}
                                </span>
                                <PagBtn id="pathology-next-page" onClick={handleNext} disabled={!hasNext} aria-label="Next page">
                                    Next
                                    <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
                                </PagBtn>
                            </div>
                        </div>
                    </div>
                )}
            </div>

        </>
    );
};

const EmptyState: React.FC<{ error: string; hasSearch: boolean; canUploadStudy: boolean }> = ({
    error, hasSearch, canUploadStudy,
}) => (
    <div className="flex flex-col items-center py-20 px-8 gap-4 text-center">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ background: error ? '#FEF2F2' : 'var(--v2-bg-surface)', border: `1px solid ${error ? '#FECACA' : 'var(--v2-border-card)'}` }}>
            {error
                ? <AlertTriangle className="w-7 h-7 text-red-500" />
                : <Microscope className="w-7 h-7 text-[var(--v2-text-muted)]" />}
        </div>
        {error ? (
            <div>
                <p className="text-base font-bold text-red-600 m-0">Unable to load studies</p>
                <p className="text-sm text-[var(--v2-text-muted)] m-0 mt-1 max-w-sm">{error}</p>
            </div>
        ) : (
            <div>
                <p className="text-base font-bold text-[var(--v2-text-secondary)] m-0">
                    {hasSearch ? 'No studies match your search' : 'No pathology studies on file'}
                </p>
                <p className="text-sm text-[var(--v2-text-muted)] m-0 mt-1 max-w-sm">
                    {hasSearch
                        ? 'Try a different patient name or MRN.'
                        : 'Upload whole-slide images (WSI) to begin building your case library.'}
                </p>
            </div>
        )}
    </div>
);

// ─── Slide Library Status badge ─────────────────────────────────────────────
const slideStatusCfg = (status: string): { bg: string; color: string; label: string } => {
    const s = (status ?? '').toUpperCase();
    if (s === 'WAITING_FOR_LAB_REPORT')
        return { bg: 'rgba(234,179,8,0.10)', color: '#92400E', label: 'Awaiting Sample' };
    if (s === 'SAMPLE_COLLECTED')
        return { bg: 'rgba(79,70,229,0.10)', color: '#4F46E5', label: 'Sample Collected' };
    if (s === 'SLIDE_READY')
        return { bg: 'rgba(5,150,105,0.10)', color: '#059669', label: 'Slide Ready' };
    if (s === 'REPORT_READY')
        return { bg: 'rgba(29,111,184,0.10)', color: '#1D6FB8', label: 'Report Ready' };
    if (s === 'REPORT_GENERATED')
        return { bg: 'rgba(29,111,184,0.10)', color: '#1D6FB8', label: 'Report Generated' };
    // fallback
    return { bg: 'rgba(107,114,128,0.08)', color: '#6B7280', label: status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) };
};

// ─── Order Status badge ───────────────────────────────────────────────────────
const orderStatusCfg = (status: string): { bg: string; color: string; label: string } => {
    const s = (status ?? '').toLowerCase();
    if (s === 'appointment_scheduled')
        return { bg: 'rgba(59,130,246,0.10)', color: '#1D6FB8', label: 'Scheduled' };
    if (s === 'pending')
        return { bg: 'rgba(234,179,8,0.10)', color: '#92400E', label: 'Pending' };
    if (s === 'processing')
        return { bg: 'rgba(124,58,237,0.10)', color: '#7C3AED', label: 'Processing' };
    if (s === 'completed')
        return { bg: 'rgba(5,150,105,0.10)', color: '#059669', label: 'Completed' };
    if (s === 'cancelled')
        return { bg: 'rgba(220,38,38,0.10)', color: '#DC2626', label: 'Cancelled' };
    return { bg: 'rgba(107,114,128,0.08)', color: '#6B7280', label: status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) };
};


const PatientStudyRow: React.FC<{
    record: StudyRecord;
    color: string;
    isEven: boolean;
    onOpen: () => void;
    onCollectSample: () => void;
    collectingSample: boolean;
}> = ({ record, color, isEven, onOpen, onCollectSample, collectingSample }) => {
    const apptDisplay = record.appointmentDateTime
        ? new Date(record.appointmentDateTime).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : '—';
    const slideStatus = slideStatusCfg(record.slideLibraryStatus);
    const orderStatus = orderStatusCfg(record.orderStatus);
    const isReportReady = ['REPORT_READY', 'REPORT_GENERATED'].includes((record.slideLibraryStatus ?? '').toUpperCase());

    // View PDF — same order-level report generation used on the order-details page:
    // generate then fetch its presigned URL and open it in a new tab.
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const handleViewPdf = async () => {
        setGeneratingPdf(true);
        try {
            const genRes: any = await LabServices.generateReportPdf(record.id);
            let openUrl = '';
            try {
                const getRes: any = await LabServices.getReportPdfUrl(record.id);
                openUrl = getRes?.reportUrl ?? getRes?.report_url ?? getRes?.url ?? getRes?.presignedUrl ?? '';
            } catch { /* GET failed — handled below */ }
            const isValidUrl = (u: string) => u.startsWith('http://') || u.startsWith('https://');
            if (openUrl && isValidUrl(openUrl)) {
                window.open(openUrl, '_blank', 'noopener,noreferrer');
                toast.success(genRes?.message ?? 'Report generated — opening in new tab');
            } else {
                const reportKey: string = genRes?.reportKey ?? genRes?.report_key ?? '';
                toast[reportKey ? 'success' : 'info'](reportKey
                    ? `${genRes?.message ?? 'Report generated'} — refresh and try again shortly.`
                    : (genRes?.message ?? 'Report generated. Try opening it again in a moment.'));
            }
        } catch (err: any) { toast.error(err?.message ?? 'PDF generation failed'); } finally { setGeneratingPdf(false); }
    };

    return (
        <div
            role="row"
            aria-label={`Patient: ${record.patientName}`}
            className="group relative border-b border-[var(--v2-border-divider)] last:border-0 transition-colors duration-100"
            style={{ background: isEven ? 'var(--v2-bg-card)' : 'var(--v2-bg-surface)' }}
        >
            <div
                className="absolute left-0 top-0 bottom-0 w-[3px] rounded-r transition-opacity duration-150 opacity-40 group-hover:opacity-100 z-10"
                style={{ background: color }}
            />

            {/* Desktop grid row — 5 data cells */}
            <div className="hidden lg:flex items-center px-6 min-h-[72px] gap-0 group-hover:bg-black/[0.018] transition-colors duration-100">
                <div className="flex-1 grid items-center min-w-0" style={{ gridTemplateColumns: '1fr 1fr 1.2fr 1.1fr 1.1fr', columnGap: 20 }}>

                    {/* Patient */}
                    <div className="min-w-0 py-3">
                        <p className="text-base font-bold text-[var(--v2-text-heading)] m-0 leading-snug truncate">
                            {record.patientName}
                        </p>
                        <p className="text-[11px] text-[var(--v2-text-muted)] m-0 mt-0.5 font-mono truncate">
                            {formatMrn(record.mrn, record.patientId)}
                        </p>
                    </div>

                    {/* Tests — count badge + hover tooltip */}
                    <div className="min-w-0 flex items-start">
                        {record.tests.length === 0 ? (
                            <span className="text-[11px] text-[var(--v2-text-muted)]">No tests</span>
                        ) : (
                            <div className="relative group/tests">
                                <span
                                    className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-lg cursor-default select-none"
                                    style={{ background: 'rgba(29,111,184,0.08)', color: '#1D6FB8', border: '1px solid rgba(29,111,184,0.18)' }}
                                >
                                    <ClipboardList className="w-[11px] h-[11px] flex-shrink-0" strokeWidth={2.5} />
                                    {record.tests.length} Test{record.tests.length !== 1 ? 's' : ''}
                                </span>
                                <div
                                    className="absolute left-0 top-full mt-1.5 z-50 min-w-[180px] max-w-[260px] rounded-xl py-2 px-3 opacity-0 pointer-events-none group-hover/tests:opacity-100 group-hover/tests:pointer-events-auto transition-opacity duration-150"
                                    style={{ background: 'var(--v2-bg-card)', border: '1px solid var(--v2-border-glass-strong)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', backdropFilter: 'blur(12px)' }}
                                >
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)] m-0 mb-1.5">Ordered Tests</p>
                                    <ul className="m-0 p-0 list-none flex flex-col gap-1">
                                        {record.tests.map((t, idx) => (
                                            <li key={t.testId} className="flex items-center gap-1.5">
                                                <span className="text-[10px] font-bold text-[var(--v2-text-muted)] w-4 flex-shrink-0">{idx + 1}.</span>
                                                <span className="text-[12px] font-semibold text-[var(--v2-text-primary)] leading-snug">{t.testName}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Appointment date */}
                    <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-[var(--v2-text-primary)] m-0 leading-snug">
                            {apptDisplay}
                        </p>
                        <p className="text-[11px] text-[var(--v2-text-muted)] m-0 mt-0.5 font-mono">
                            Ordered: {record.orderDate ?? '—'}
                        </p>
                    </div>

                    {/* Order Status badge */}
                    <div className="min-w-0">
                        <span
                            className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-lg"
                            style={{ background: orderStatus.bg, color: orderStatus.color, border: `1px solid ${orderStatus.color}28` }}
                        >
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: orderStatus.color }} />
                            {orderStatus.label}
                        </span>
                    </div>

                    {/* Lab (Slide Library) Status badge */}
                    <div className="min-w-0">
                        <span
                            className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-lg"
                            style={{ background: slideStatus.bg, color: slideStatus.color, border: `1px solid ${slideStatus.color}28` }}
                        >
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: slideStatus.color }} />
                            {slideStatus.label}
                        </span>
                    </div>
                </div>

                {/* ── Actions column — both always shown, disabled until ready ── */}
                {/* Width MUST match header w-[160px] exactly — no ml margin */}
                <div className="w-[160px] flex-shrink-0 flex items-center gap-1.5">

                    {/* View Slide — always rendered, disabled until studyInstanceUID is available */}
                    <button
                        type="button"
                        onClick={onOpen}
                        disabled={!record.canViewSlide || !record.studyInstanceUID}
                        aria-label="View slide in viewer"
                        title={record.canViewSlide && record.studyInstanceUID ? 'Open in slide viewer' : 'Slide not yet available'}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[12px] font-bold transition-colors duration-150 cursor-pointer disabled:opacity-35 disabled:cursor-not-allowed active:scale-[0.97]"
                        style={{ background: `${color}14`, color, border: `1.5px solid ${color}30` }}
                    >
                        <Eye className="w-[13px] h-[13px]" strokeWidth={2} />
                        View Slide
                    </button>

                    {/* View PDF — always rendered, disabled until the report is actually ready */}
                    <button
                        type="button"
                        onClick={handleViewPdf}
                        disabled={!isReportReady || generatingPdf}
                        aria-label="View report PDF"
                        title={isReportReady ? 'View lab report PDF' : 'Report not generated yet'}
                        className="flex-shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-xl text-[12px] font-bold transition-colors duration-150 cursor-pointer disabled:opacity-35 disabled:cursor-not-allowed active:scale-[0.97]"
                        style={{ background: 'rgba(5,150,105,0.10)', color: '#059669', border: '1.5px solid rgba(5,150,105,0.28)' }}
                    >
                        {generatingPdf
                            ? <Loader2 className="w-[13px] h-[13px] animate-spin" strokeWidth={2} />
                            : <FileText className="w-[13px] h-[13px]" strokeWidth={1.8} />}
                    </button>
                </div>
            </div>

            {/* ── MOBILE ROW ──────────────────────────────────────────────── */}
            <div className="lg:hidden px-4 py-3.5">
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-[14px] font-bold text-[var(--v2-text-heading)] m-0 truncate">{record.patientName}</p>
                        <p className="text-[10px] text-[var(--v2-text-muted)] m-0 font-mono truncate">
                            {formatMrn(record.mrn, record.patientId)}
                        </p>
                    </div>
                    {/* Single workflow status badge — mobile header */}
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span
                            className="text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                            style={{ background: orderStatus.bg, color: orderStatus.color }}
                        >
                            <span className="w-1 h-1 rounded-full" style={{ background: orderStatus.color }} />
                            {orderStatus.label}
                        </span>
                        <span
                            className="text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                            style={{ background: slideStatus.bg, color: slideStatus.color }}
                        >
                            <span className="w-1 h-1 rounded-full" style={{ background: slideStatus.color }} />
                            {slideStatus.label}
                        </span>
                    </div>
                </div>

                {/* Tests — count badge (same pattern as desktop) */}
                {record.tests.length > 0 && (
                    <div className="flex items-center gap-1.5 pt-2 mt-2" style={{ borderTop: '1px solid var(--v2-border-subtle)' }}>
                        <span
                            className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-lg"
                            style={{ background: 'rgba(29,111,184,0.08)', color: '#1D6FB8', border: '1px solid rgba(29,111,184,0.18)' }}
                        >
                            <ClipboardList className="w-[11px] h-[11px] flex-shrink-0" strokeWidth={2.5} />
                            {record.tests.length} Test{record.tests.length !== 1 ? 's' : ''}
                        </span>
                        {/* Inline test names (up to 2, rest as +N) */}
                        <span className="text-[11px] text-[var(--v2-text-muted)] truncate">
                            {record.tests.slice(0, 2).map(t => t.testName).join(', ')}
                            {record.tests.length > 2 && ` +${record.tests.length - 2} more`}
                        </span>
                    </div>
                )}

                {/* Appt date + orderDate sub-line */}
                <div className="mt-1.5">
                    <p className="text-[11px] text-[var(--v2-text-muted)] m-0 truncate">
                        <CalendarDays className="inline w-3 h-3 mr-1" strokeWidth={2} />{apptDisplay}
                    </p>
                    <p className="text-[10px] text-[var(--v2-text-muted)] m-0 mt-0.5 font-mono truncate">
                        Ordered: {record.orderDate ?? '—'}
                    </p>
                </div>

                {/* Actions (mobile) — both always shown, disabled until ready */}
                <div className="flex items-center gap-2 pt-2 mt-2" style={{ borderTop: '1px solid var(--v2-border-subtle)' }}>
                    <button type="button" onClick={onOpen}
                        disabled={!record.canViewSlide || !record.studyInstanceUID}
                        title={record.canViewSlide && record.studyInstanceUID ? 'Open in slide viewer' : 'Slide not yet available'}
                        className="flex-1 inline-flex items-center justify-center gap-1 py-2 rounded-xl text-[11px] font-bold cursor-pointer disabled:opacity-35 disabled:cursor-not-allowed active:scale-[0.97]"
                        style={{ background: `${color}14`, color, border: `1px solid ${color}30` }}
                    >
                        <Eye className="w-[12px] h-[12px]" strokeWidth={2} /> View Slide
                    </button>
                    <button type="button" onClick={handleViewPdf}
                        disabled={!isReportReady || generatingPdf}
                        title={isReportReady ? 'View lab report PDF' : 'Report not generated yet'}
                        className="flex-shrink-0 w-9 h-9 inline-flex items-center justify-center gap-1 py-2 rounded-xl text-[11px] font-bold cursor-pointer disabled:opacity-35 disabled:cursor-not-allowed active:scale-[0.97]"
                        style={{ background: 'rgba(5,150,105,0.10)', color: '#059669', border: '1px solid rgba(5,150,105,0.25)' }}
                    >
                        {generatingPdf
                            ? <Loader2 className="w-[12px] h-[12px] animate-spin" strokeWidth={2} />
                            : <FileText className="w-[12px] h-[12px]" strokeWidth={1.8} />}
                    </button>
                </div>
            </div>
        </div>
    );
};

const SPECIMEN_TYPES = [
    'Blood', 'Urine', 'Tissue Biopsy', 'Fine Needle Aspirate (FNA)',
    'Body Fluid', 'Swab / Smear', 'Bone Marrow', 'Stool / Feces', 'Other',
];

const CONTAINER_TYPES = [
    { label: 'EDTA Tube (Purple)', value: 'EDTA Tube' },
    { label: 'SST / Serum Tube (Gold)', value: 'SST Tube' },
    { label: 'Heparin Tube (Green)', value: 'Heparin Tube' },
    { label: 'Citrate Tube (Blue)', value: 'Citrate Tube' },
    { label: 'Fluoride Tube (Grey)', value: 'Fluoride Tube' },
    { label: 'Formalin Container', value: 'Formalin Container' },
    { label: 'Dry / Plain Container', value: 'Dry Container' },
    { label: 'Sterile Universal Container', value: 'Universal Container' },
    { label: 'Other', value: 'Other' },
];

const SField: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--v2-text-muted)' }}>
            {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {children}
    </div>
);

const sinputCls = [
    'w-full rounded-xl border bg-[var(--v2-bg-surface)] px-3 py-2.5',
    'text-[13px] text-[var(--v2-text-primary)] outline-none transition-colors',
    'border-[var(--v2-border-interactive)] focus:border-[#7C3AED]',
].join(' ');

const CollectSpecimenModal: React.FC<{
    record: StudyRecord;
    onClose: () => void;
    onSuccess: () => void;
}> = ({ record, onClose, onSuccess }) => {

    const nowLocal = () => {
        const d = new Date();
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:MM"
    };

    const [form, setForm] = useState({
        testId: record.tests.length === 1 ? record.tests[0].testId : '',
        sampleTypeName: '',
        bottleType: '',
        collectedBy: '',
        collectedMinQty: '',
        collectedMaxQty: '',
    });
    const [saving, setSaving] = useState(false);
    const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
        setForm(f => ({ ...f, [k]: e.target.value }));

    const handleSubmit = async () => {
        if (!form.testId) { toast.error('Please select a test for this specimen'); return; }
        if (!form.sampleTypeName) { toast.error('Please select a specimen type'); return; }
        if (!form.bottleType) { toast.error('Please select a container / tube type'); return; }
        if (!form.collectedBy.trim()) { toast.error('Please enter the collector name'); return; }

        setSaving(true);
        try {
            // Step 1 — Register specimen via Lab API
            await LabServices.registerSample(record.id, {
                testId: form.testId,
                sampleTypeName: form.sampleTypeName,
                bottleType: form.bottleType,
                collectedBy: form.collectedBy.trim(),
                ...(form.collectedMinQty.trim() && { collectedMinQty: form.collectedMinQty.trim() }),
                ...(form.collectedMaxQty.trim() && { collectedMaxQty: form.collectedMaxQty.trim() }),
            });

            // Step 2 — Update slide library status to SAMPLE_COLLECTED
            await PathologyServices.updateSlideLibraryStatus(record.id, 'SAMPLE_COLLECTED');

            toast.success(`Specimen registered for ${record.patientName}`);
            onSuccess();
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? err?.message ?? 'Failed to register specimen');
        } finally {
            setSaving(false);
        }
    };

    const color = '#7C3AED';

    return createPortal(
        <div
            className="fixed inset-0 z-[300] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="w-full max-w-lg rounded-2xl flex flex-col max-h-[90vh] overflow-hidden"
                style={{ background: 'var(--v2-bg-card)', border: '1px solid var(--v2-border-card)', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>

                {/* Colored accent strip */}
                <div className="h-1.5 flex-shrink-0" style={{ background: `linear-gradient(90deg, ${color}, #1D6FB8)` }} />

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--v2-border-subtle)' }}>
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ background: `rgba(124,58,237,0.12)` }}>
                            <FlaskConical className="w-[18px] h-[18px]" style={{ color }} strokeWidth={1.8} />
                        </div>
                        <div>
                            <p className="text-[14px] font-black m-0" style={{ color: 'var(--v2-text-heading)' }}>Register Specimen</p>
                            <p className="text-[11px] m-0 mt-0.5" style={{ color: 'var(--v2-text-muted)' }}>
                                {record.patientName} · MRN {record.mrn ?? '—'}
                            </p>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close"
                        className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer border-0 bg-transparent hover:bg-[var(--v2-bg-surface)] transition-colors"
                        style={{ color: 'var(--v2-text-muted)' }}>
                        <X className="w-4 h-4" strokeWidth={2} />
                    </button>
                </div>

                {/* Patient + Tests summary banner */}
                <div className="px-5 py-3 flex flex-wrap items-center gap-2 border-b flex-shrink-0"
                    style={{ background: 'rgba(124,58,237,0.04)', borderColor: 'var(--v2-border-subtle)' }}>
                    <span className="text-[11px] font-bold" style={{ color: 'var(--v2-text-muted)' }}>Tests:</span>
                    {record.tests.length === 0
                        ? <span className="text-[11px]" style={{ color: 'var(--v2-text-muted)' }}>—</span>
                        : record.tests.map(t => (
                            <span key={t.testId}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-lg"
                                style={{ background: `rgba(124,58,237,0.10)`, color }}>
                                <TestTube2 className="w-[10px] h-[10px]" strokeWidth={2} />
                                {t.testName}
                            </span>
                        ))
                    }
                </div>

                {/* Form body */}
                <div className="overflow-y-auto flex-1 px-5 py-5 flex flex-col gap-4">

                    {/* Row 0 — Test selector (required) */}
                    <SField label="Test" required>
                        <select className={sinputCls} value={form.testId} onChange={set('testId')}>
                            <option value="">— Select test —</option>
                            {record.tests.map(t => <option key={t.testId} value={t.testId}>{t.testName}</option>)}
                        </select>
                    </SField>

                    {/* Row 1 — Specimen Type + Container */}
                    <div className="grid grid-cols-2 gap-3">
                        <SField label="Specimen Type" required>
                            <select className={sinputCls} value={form.sampleTypeName} onChange={set('sampleTypeName')}>
                                <option value="">— Select —</option>
                                {SPECIMEN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </SField>
                        <SField label="Container / Tube" required>
                            <select className={sinputCls} value={form.bottleType} onChange={set('bottleType')}>
                                <option value="">— Select —</option>
                                {CONTAINER_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </SField>
                    </div>

                    {/* Row 2 — Collected By */}
                    <SField label="Collected By" required>
                        <input
                            className={sinputCls}
                            placeholder="Phlebotomist / Tech name"
                            value={form.collectedBy}
                            onChange={set('collectedBy')}
                        />
                    </SField>

                    {/* Row 3 — Min Qty + Max Qty */}
                    <div className="grid grid-cols-2 gap-3">
                        <SField label="Min. Quantity">
                            <input
                                className={sinputCls}
                                placeholder="e.g. 3 mL"
                                value={form.collectedMinQty}
                                onChange={set('collectedMinQty')}
                            />
                        </SField>
                        <SField label="Max. Quantity">
                            <input
                                className={sinputCls}
                                placeholder="e.g. 10 mL"
                                value={form.collectedMaxQty}
                                onChange={set('collectedMaxQty')}
                            />
                        </SField>
                    </div>

                    {/* Required fields note */}
                    <p className="text-[10px] m-0" style={{ color: 'var(--v2-text-muted)' }}>
                        <span className="text-red-500">*</span> Required fields
                    </p>
                </div>

                {/* Footer */}
                <div className="px-5 py-4 border-t flex items-center justify-between gap-3 flex-shrink-0"
                    style={{ borderColor: 'var(--v2-border-subtle)', background: 'var(--v2-bg-surface)' }}>
                    <div className="flex items-center gap-1.5" style={{ color: 'var(--v2-text-muted)' }}>
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.8} />
                        <span className="text-[10px] font-semibold">Appt: {record.appointmentDateTime
                            ? new Date(record.appointmentDateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '—'}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} disabled={saving}
                            className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer disabled:opacity-50 transition-colors border"
                            style={{ border: '1px solid var(--v2-border-card)', color: 'var(--v2-text-muted)', background: 'var(--v2-bg-card)' }}>
                            Cancel
                        </button>
                        <button type="button" onClick={handleSubmit} disabled={saving}
                            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white border-0 disabled:opacity-50 transition-opacity hover:opacity-90"
                            style={{ background: `linear-gradient(135deg, ${color}, #1D6FB8)` }}>
                            {saving
                                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2.5} /> Submitting…</>
                                : <><Save className="w-3.5 h-3.5" strokeWidth={2} /> Register Specimen</>
                            }
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

const PagBtn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ children, ...props }) => (
    <button
        type="button"
        {...props}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[var(--v2-border-subtle)] bg-[var(--v2-bg-card)] text-sm font-semibold text-[var(--v2-text-secondary)] cursor-pointer disabled:opacity-35 disabled:cursor-not-allowed hover:text-[var(--v2-text-primary)] transition-colors"
    >
        {children}
    </button>
);

export default SlideLibrary;
